import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { AppState } from 'src/app/store/app.reducer';
import { selectCurrentDevice, selectCurrentDeviceCommands, selectCurrentDeviceFeatures, selectCurrentDeviceModel, selectCurrentDeviceStatus, selectDeviceTimerInfo, selectDeviceTimerModify, selectDeviceTimers, selectPasswordValid } from 'src/app/store/device/device.selector';
import { TranslateService } from '@ngx-translate/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { LIST_TIMERS_PAGE_DEFAULT_SIZE, WEEK_DAYS_TIMERS } from 'src/app/conf/timers';
import { Device } from 'src/app/model/device';
import { DeviceStatus, isAutomFwUpdateInProgress, isDeviceUnavailable } from 'src/app/model/device-status';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { DeviceTimerDialogComponent } from '../device-timer-dialog/device-timer-dialog.component';
import { DeviceTimers, DeviceTimersInfo } from 'src/app/model/device-timer';
import { ConfirmationService } from 'primeng/api';
import { Command } from 'src/app/model/command';
import { StandardMtmaLayout, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { DeviceFeature } from 'src/app/model/device-feature';
import { selectIsImpersMode, selectRoleExplainedTechnician } from 'src/app/store/user/user.selector';
import { PasswordDialogComponent } from '../password-dialog/password-dialog.component';
import { DeviceModel } from 'src/app/model/device-model';
import { EDeviceModel } from 'src/app/conf/device-models';

@Component({
	selector: 'app-timers-list-timers',
	templateUrl: './timers-list-timers.component.html',
	providers: [ConfirmationService, DialogService]
})

export class TimersListTimersComponent implements OnInit, OnDestroy {
	device$: Observable<Device | undefined>;
	deviceSub: Subscription | undefined;
	deviceStatus$: Observable<DeviceStatus | undefined>;
	deviceStatusSub: Subscription | undefined;
	timersInfo$: Observable<DeviceTimersInfo | undefined>;
	timersInfoSub: Subscription | undefined;
	timers$: Observable<DeviceTimers[] | undefined>;
	timersSub: Subscription | undefined;
	commands$: Observable<Command[] | undefined>;
	commandsSub: Subscription | undefined;
	timerModify$: Observable<boolean | undefined>;
	timerModifySub: Subscription | undefined;
	deviceModel$: Observable<DeviceModel | undefined>;
	deviceModelSub: Subscription | undefined;

	deviceFeature$: Observable<DeviceFeature[] | undefined>;
	deviceFeatureSub: Subscription | undefined;
	passwordVerify$: Observable<boolean | undefined>;
	passwordVerifySub: Subscription | undefined;
	isTechnician$: Observable<boolean | undefined>;
	isTechnicianSub: Subscription | undefined;
	isImpersMode$: Observable<boolean | undefined>;
	isImpersModeSub: Subscription | undefined;

	passwordSupport: boolean;
	passwordVerify: boolean;
	isTechnician: boolean;
	isImpersMode: boolean;

	loading: boolean;
	online: boolean;
	deviceStatus: DeviceStatus;
	timerModify: boolean;
	deviceModel: DeviceModel;

	timers: DeviceTimers[];
	device: Device = new Device();
	timersInfo: DeviceTimersInfo;
	opened = false;
	totalRecords = 0;
	commands: Command[];

	pageSize = LIST_TIMERS_PAGE_DEFAULT_SIZE;

	showDiv = false;
	ref: DynamicDialogRef | undefined;
	callRest = false;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	memorySize: number;
	maxMemorySize: number;

	screenWidth: number;

	constructor(
		private store: Store<AppState>,
		private fb: FormBuilder,
		private translate: TranslateService,
		public dialogService: DialogService,
		public confirmationService: ConfirmationService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		// Store selectors
		this.device$ = this.store.select(selectCurrentDevice);
		this.deviceStatus$ = this.store.select(selectCurrentDeviceStatus);
		this.timersInfo$ = this.store.select(selectDeviceTimerInfo);
		this.timers$ = this.store.select(selectDeviceTimers);
		this.commands$ = this.store.select(selectCurrentDeviceCommands);
		this.timerModify$ = this.store.select(selectDeviceTimerModify);
		this.deviceFeature$ = this.store.select(selectCurrentDeviceFeatures);
		this.passwordVerify$ = this.store.select(selectPasswordValid);
		this.isTechnician$ = this.store.select(selectRoleExplainedTechnician);
		this.isImpersMode$ = this.store.select(selectIsImpersMode);
		this.deviceModel$ = this.store.select(selectCurrentDeviceModel);
	}

	ngOnInit(): void {
		this.callRest = false;
		this.getTimersInfo();
		this.deviceSub = this.device$.subscribe(currentDevice => {
			this.device = currentDevice;
		});
		this.deviceStatusSub = this.deviceStatus$.subscribe(deviceStatus => {
			if (deviceStatus !== undefined) {
				const wasAvailable = this.online;
				const nowAvailable = !isDeviceUnavailable(deviceStatus);
				if (nowAvailable && !wasAvailable) {
					this.getTimersInfo();
				}
				this.deviceStatus = deviceStatus;
				this.online = nowAvailable;
			}
		});
		this.timersInfoSub = this.timersInfo$.subscribe(timersInfo => {
			this.timersInfo = timersInfo;
			if (timersInfo !== undefined) {
				this.memorySize = timersInfo.Used;
				this.maxMemorySize = timersInfo.Max;
				this.loadTimers();
				for (const command of this.timersInfo.ControlItem.Items) {
					command.DescriptionItem = this.getDescriptionItem(command);
				}
			}
		});
		this.timersSub = this.timers$.subscribe(timers => {
			if (timers !== undefined && timers.length > 0) {
				this.timers = this.timers.concat(timers);
				this.totalRecords = this.timers.length;
				if (this.timers.length < this.memorySize) {
					this.getListTimers(this.timers[this.timers.length - 1].Position);
				} else {
					this.loading = false;
					this.callRest = false;
				}
			}
		});
		this.commandsSub = this.commands$.subscribe(
			commands => {
				this.commands = commands;
			}
		);
		this.timerModifySub = this.timerModify$.subscribe(
			timerModify => {
				this.timerModify = timerModify;
				if (this.timerModify) {
					this.getTimersInfo();
				}
			}
		);
		this.deviceFeatureSub = this.deviceFeature$.subscribe(deviceFeatures => {
			const fea = deviceFeatures.find(fe => fe.Name === 'password');
			if (fea !== undefined) {
				this.passwordSupport = true;
			}
		});
		this.passwordVerifySub = this.passwordVerify$.subscribe(passwordVerify => {
			this.passwordVerify = passwordVerify;
			if (this.passwordVerify) {
				this.loadTimers();
			}
		});
		this.isTechnicianSub = this.isTechnician$.subscribe((isTechnician => {
			this.isTechnician = isTechnician;
		}));
		this.isImpersModeSub = this.isImpersMode$?.subscribe(
			(isImpersMode) => {
				this.isImpersMode = isImpersMode;
			}
		);
		this.deviceModelSub = this.deviceModel$.subscribe(deviceModel => {
			this.deviceModel = deviceModel;
			if (this.timersInfo !== undefined) {
				for (const command of this.timersInfo.ControlItem.Items) {
					command.DescriptionItem = this.getDescriptionItem(command);
				}
			}
		});
		this.onResize();
	}

	ngOnDestroy(): void {
		this.deviceSub?.unsubscribe();
		this.deviceStatusSub?.unsubscribe();
		this.timersInfoSub?.unsubscribe();
		this.commandsSub?.unsubscribe();
		this.deviceFeatureSub?.unsubscribe();
		this.passwordVerifySub?.unsubscribe();
		this.isTechnicianSub?.unsubscribe();
		this.isImpersModeSub?.unsubscribe();
		this.deviceModelSub?.unsubscribe();
	}

	togglePanel(opened: boolean): void {
		this.opened = opened;
		if (this.opened && this.online) {
			this.getTimersInfo();
			this.loadTimers();
		}
	}

	loadTimers(): void {
		if (this.opened && this.online) {
			if (!this.passwordSupport || this.passwordVerify || (!this.isTechnician && !this.isImpersMode)) {
				this.loadTimersRequest();
			} else {
				/* Open Dialog */
				this.ref = this.dialogService.open(PasswordDialogComponent, {
					header: this.translate.instant('PASSWORD'),
					width: '70vw',
					breakpoints: { '1600px': '98vw' },
					contentStyle: { overflow: 'auto' },
				});
			}
		}
	}

	getTimersInfo(): void {
		if (this.opened && this.online) {
			this.store.dispatch(DeviceActions.getDeviceTimersInfo());
		}
	}

	loadTimersRequest(): void {
		if (this.opened && this.online) {
			if (this.memorySize > 0) {
				this.timers = [];
				this.loading = true;
				if (!this.callRest) {
					this.callRest = true;
					this.getListTimers(0);
				}
			} else {
				this.loading = false;
				this.timers = [];
			}
		}
	}

	getListTimers(from: number): void {

		this.store.dispatch(DeviceActions.getDeviceTimers({ from: from }));
	}

	addTimer(): void {
		this.store.dispatch(DeviceActions.resetDeviceTimerForm());
		this.ref = this.dialogService.open(DeviceTimerDialogComponent, {
			header: this.translate.instant('ADD_TIMER'),
			width: '70vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
		});
	}

	editTimer(timer: DeviceTimers): void {
		this.store.dispatch(DeviceActions.resetDeviceTimerForm());
		this.ref = this.dialogService.open(DeviceTimerDialogComponent, {
			header: this.translate.instant('EDIT_TIMER'),
			width: '70vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
			data: { timer },
		});
	}

	deleteTimer(timer: DeviceTimers): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_TIMER'),
			message: this.translate.instant('DELETE_TIMER_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(DeviceActions.deleteDeviceTimer({ position: timer.Position }));
			},
			reject: () => {
			}
		});
	}

	removeAll(): void {
		this.acceptButtonStyle = "p-button-danger";
		this.rejectButtonStyle = "p-button-secondary";
		this.confirmationService.confirm({
			header: this.translate.instant('DELETE_TIMERS'),
			message: this.translate.instant('DELETE_TIMERS_WARNING'),
			acceptLabel: this.translate.instant('CONFIRM'),
			rejectLabel: this.translate.instant('CANCEL'),
			icon: 'pi cm-alert',
			accept: () => {
				this.store.dispatch(DeviceActions.deleteDeviceAllTimer());
			},
			reject: () => {
			}
		});
	}

	getValueMemory(): number {
		return Math.ceil((this.memorySize / this.maxMemorySize) * 100);
	}

	getFunction(timer: DeviceTimers): string {
		if (timer !== undefined && this.timersInfo !== undefined && this.timersInfo.ControlItem !== undefined && this.timersInfo.ControlItem.Items !== undefined) {
			const func = this.timersInfo.ControlItem.Items.find(item => item.Value === timer.Mode);
			if (func !== undefined) {
				return func.DescriptionItem;
			}
		}
		return '';
	}

	getDays(timer: DeviceTimers): string {
		let strRes = '';
		let dayBitmask = timer.WeekMask;
		for (let i = 7; i >= 0; i--) {
			if (Math.floor(dayBitmask / (2 ** i)) === 1) {
				dayBitmask = dayBitmask - (2 ** i);
				if (strRes !== '') {
					strRes = ', ' + strRes;
				}
				strRes = this.getStringDay(i) + strRes;
			}
		}
		return strRes;
	}

	getStringDay(bit: number): string {
		const b = WEEK_DAYS_TIMERS.find(el => el.Value === bit);
		if (b !== undefined) {
			return this.translate.instant(b.Name);
		}
		return '';
	}

	getTimeStart(timer: DeviceTimers): string {
		if (timer.StartMin.toString().length === 1) {
			return timer.StartHour + ':0' + timer.StartMin;
		}
		return timer.StartHour + ':' + timer.StartMin;
	}

	getTimeEnd(timer: DeviceTimers): string {
		if (timer.EndMin.toString().length === 1) {
			return timer.EndHour + ':0' + timer.EndMin;
		}
		return timer.EndHour + ':' + timer.EndMin;
	}

	isAccess(): boolean {
		if (!this.passwordSupport) {
			return true;
		}
		if (this.passwordVerify) {
			return true;
		}
		if (!this.isTechnician && !this.isImpersMode) {
			return true;
		}
		return false;
	}

	isAutomFwUpdateInProgress(): boolean {
		return isAutomFwUpdateInProgress(this.deviceStatus);
	}

	unavailableAlertKey(): string {
		return this.isAutomFwUpdateInProgress() ? 'DEVICE_FW_UPDATE_IN_PROGRESS_ALERT' : 'OFFLINE_ALERT';
	}


	isRbe24230(device: DeviceModel): boolean {
		return device !== undefined && (device.Id === EDeviceModel.RBE24230 || device.Id === EDeviceModel.RBE230 ||
			device.Id === EDeviceModel.RBE24 || device.Id === EDeviceModel.GEN_RBE24230);
	}

	getDescriptionItem(item: any): string {
		if (item === undefined) {
			return '';
		}
		if (this.isRbe24230(this.deviceModel)) {
			for (const command of this.commands) {
				if (item.Value === 16 && command.Id === 144) {
					return command.Default === command.Description ?
						item.Description : command.Description + ' (' + command.Default + ')';
				}
				if (item.Value === 32 && command.Id === 145) {
					return command.Default === command.Description ?
						item.Description : command.Description + ' (' + command.Default + ')';
				}
				if (item.Value === 64 && command.Id === 146) {
					return command.Default === command.Description ?
						item.Description : command.Description + ' (' + command.Default + ')';
				}
				if (item.Value === 128 && command.Id === 147) {
					return command.Default === command.Description ?
						item.Description : command.Description + ' (' + command.Default + ')';
				}
			}
			return item.Description;
		} else {
			return item.Description;
		}
	}

	// Design
	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	actionLayout(position: string): string {
		return StandardMtmaLayout.actionLayout(this.screenWidth, position);
	}

	layoutMarginResponsive(): string {
		return StandardPageLayout.layoutMarginResponsive(this.screenWidth);
	}
}
